import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import './CalibrationModal.css';

function CalibrationModal({ review, assessment, isOpen, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviewData, setReviewData] = useState(null);
  const [userRole, setUserRole] = useState(null);

  // Fetch review data when review or assessment props change
  useEffect(() => {
    // Support both prop structures: new {review} and legacy {assessment, isOpen}
    const currentReview = review || assessment;
    const isModalOpen = review ? true : isOpen;

    if (currentReview && currentReview.email && currentReview.cycleId && isModalOpen) {
      const fetchReviewData = async () => {
        try {
          setLoading(true);
          setError(null);
          setReviewData(null);

          // Get user role
          const role = sessionStorage.getItem('access_role') || 'user';
          setUserRole(role);

          console.log('Fetching review data for email:', currentReview.email, 'cycleId:', currentReview.cycleId);

          // Call the API to get all reviews for this email and cycle combination
          console.log('Making API call...');
          const response = await apiService.fetchData(`employee-reviews?type=employee&email=${currentReview.email}&cycle_id=${currentReview.cycleId}`);
          console.log('API response received:', response);

            if (response && response.status === 'success' && response.data && response.data.length > 0) {
            const cycleData = response.data[0]; // Get the first (and should be only) cycle
            const employeeData = cycleData.employees[0]; // Get the first (and should be only) employee

            console.log('Review data:', employeeData);

            // Get reviews for comments and other data
            const reviews = employeeData.reviews;

            // Use API-provided weighted averages instead of manual calculation
            const avgSelfScore = employeeData.self_average_rating;
            const avgManagerScore = employeeData.manager_average_rating;
            const difference = avgSelfScore && avgManagerScore ? (parseFloat(avgManagerScore) - parseFloat(avgSelfScore)).toFixed(2) : null;

            // Create capabilities breakdown grouped by category
            const capabilitiesByCategory = {};
            reviews.forEach(reviewItem => {
              const categoryName = reviewItem.CategoryName || 'General';
              if (!capabilitiesByCategory[categoryName]) {
                capabilitiesByCategory[categoryName] = {
                  name: categoryName,
                  self: [],
                  manager: []
                };
              }
              if (reviewItem.SelfRating !== null && reviewItem.SelfRating !== undefined) {
                capabilitiesByCategory[categoryName].self.push(reviewItem.SelfRating);
              }
              if (reviewItem.ManagerRating !== null && reviewItem.ManagerRating !== undefined) {
                capabilitiesByCategory[categoryName].manager.push(reviewItem.ManagerRating);
              }
            });

            // Calculate category averages
            const capabilities = Object.values(capabilitiesByCategory).map(cat => ({
              name: cat.name,
              self: cat.self.length > 0 ? (cat.self.reduce((a, b) => a + b, 0) / cat.self.length).toFixed(2) : null,
              manager: cat.manager.length > 0 ? (cat.manager.reduce((a, b) => a + b, 0) / cat.manager.length).toFixed(2) : null,
              diff: (cat.self.length > 0 && cat.manager.length > 0) ?
                ((cat.manager.reduce((a, b) => a + b, 0) / cat.manager.length) - (cat.self.reduce((a, b) => a + b, 0) / cat.self.length)).toFixed(2) : null
            }));

            // Transform comments with question information
            const selfComments = reviews
              .filter(reviewItem => reviewItem.SelfComment && reviewItem.SelfComment.trim() !== '')
              .map(reviewItem => ({
                capability: reviewItem.CategoryName || 'Assessment',
                question: reviewItem.Question || `Question ID: ${reviewItem.QuestionID}`,
                questionId: reviewItem.QuestionID,
                comment: reviewItem.SelfComment
              }));

            const managerComments = reviews
              .filter(reviewItem => reviewItem.ManagerComment && reviewItem.ManagerComment.trim() !== '')
              .map(reviewItem => ({
                capability: reviewItem.CategoryName || 'Assessment',
                question: reviewItem.Question || `Question ID: ${reviewItem.QuestionID}`,
                questionId: reviewItem.QuestionID,
                comment: reviewItem.ManagerComment
              }));

            // Transform action items with question information
            const actionItems = reviews
              .filter(reviewItem => reviewItem.ActionItem && reviewItem.ActionItem.trim() !== '')
              .map(reviewItem => ({
                capability: reviewItem.CategoryName || 'Assessment',
                question: reviewItem.Question || `Question ID: ${reviewItem.QuestionID}`,
                questionId: reviewItem.QuestionID,
                actionItem: reviewItem.ActionItem
              }));

            // Transform the data for display
            const transformedReview = {
              name: employeeData.email.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
              initials: employeeData.email.split('@')[0].split(/[._]/).map(n => n[0]).join('').toUpperCase(),
              role: reviews[0]?.Role || 'Unknown Role',
              submissionDate: reviews[0]?.SelfSubmittedAt ? new Date(reviews[0].SelfSubmittedAt).toLocaleDateString() : 'Not submitted',
              selfScore: avgSelfScore,
              managerScore: avgManagerScore,
              difference: difference,
              capabilities: capabilities,
              selfComments: selfComments.length > 0 ? selfComments : [{ capability: 'All Capabilities', question: '', questionId: null, comment: 'No comments submitted.' }],
              managerComments: managerComments.length > 0 ? managerComments : [{ capability: 'All Capabilities', question: '', questionId: null, comment: 'No comments provided.' }],
              actionItems: actionItems.length > 0 ? actionItems : [{ capability: 'All Capabilities', question: '', questionId: null, actionItem: 'No action items assigned.' }],
              is_published: employeeData.is_published || false // Track if manager assessment is published
            };

            setReviewData(transformedReview);
          } else {
            console.log('API response format issue:', response);
            setError(response?.message || 'No review data found for this email and cycle combination');
          }

        } catch (err) {
          console.error('Error fetching review:', err);
          setError(err.message || 'Failed to load review details');
        } finally {
          console.log('Setting loading to false');
          setLoading(false);
        }
      };

      fetchReviewData();
    } else {
      // If review props are not provided or invalid, set loading to false
      console.log('Review props not provided or invalid:', { review, assessment, isOpen });
      setLoading(false);
    }
  }, [review, assessment, isOpen]);

  const handleSave = () => {
    // Close the modal
    onClose();
  };

  // Don't render if modal is not open (for legacy prop structure)
  if (assessment && !isOpen) return null;

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content">
          <div className="loading-container">
            <p>Loading review details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !reviewData) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content">
          <div className="error-container">
            <p>{error || 'Review not found or access denied.'}</p>
            <button onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <div className="associate-info">
            <div className="associate-initials">{reviewData.initials}</div>
            <div className="associate-details">
              <h2>{reviewData.name} - Calibration Review</h2>
              <p>{reviewData.role} | Submitted on: {reviewData.submissionDate}</p>
            </div>
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {/* Summary Section */}
        <div className="summary-section">
          <div className="summary-card">
            <div className="summary-label">Self Score</div>
            <div className="summary-value self-score">{reviewData.selfScore || 'N/A'}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Manager Score</div>
            <div className="summary-value manager-score">
              {(() => {
                // For users and manager's "My Assessment" view, hide score if not published
                const shouldHideScore = (userRole === 'user' || (userRole === 'manager' && assessment?.managerFilter === 'My Assessment')) && !reviewData.is_published;
                const hasManagerRating = reviewData.managerScore !== null && reviewData.managerScore !== undefined;
                
                if (shouldHideScore) {
                  // Show locked icon with "Given" if manager rating exists but not published
                  return hasManagerRating ? '🔒 Given' : 'N/A';
                }
                return reviewData.managerScore || 'N/A';
              })()}
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Difference</div>
            <div className={`summary-value difference-score ${reviewData.difference >= 0 ? 'positive' : 'negative'}`}>
              {(() => {
                // Hide difference if manager score is hidden
                const shouldHideScore = (userRole === 'user' || (userRole === 'manager' && assessment?.managerFilter === 'My Assessment')) && !reviewData.is_published;
                if (shouldHideScore) {
                  return '🔒 Locked';
                }
                return (reviewData.difference > 0 ? '+' : '') + (reviewData.difference || 'N/A');
              })()}
            </div>
          </div>
        </div>

        {/* Capability Breakdown */}
        <div className="capability-breakdown">
          <h3>Capability Breakdown</h3>
          <div className="breakdown-table">
            <div className="table-header">
              <div className="header-cell">Capability</div>
              <div className="header-cell" title="The score reflects the average rating of all questions under this capability.">Self</div>
              <div className="header-cell" title="The score reflects the average rating of all questions under this capability.">Manager</div>
              <div className="header-cell">Diff</div>
            </div>
            {reviewData.capabilities.map((capability, index) => {
              // Check if manager scores should be hidden
              const shouldHideScore = (userRole === 'user' || (userRole === 'manager' && assessment?.managerFilter === 'My Assessment')) && !reviewData.is_published;
              const hasManagerRating = capability.manager !== null && capability.manager !== undefined;
              
              return (
                <div key={index} className="table-row">
                  <div className="table-cell capability-name">{capability.name}</div>
                  <div className="table-cell self-score">{capability.self || 'N/A'}</div>
                  <div className="table-cell manager-score">
                    {shouldHideScore ? (hasManagerRating ? '🔒 Given' : 'N/A') : (capability.manager || 'N/A')}
                  </div>
                  <div className={`table-cell difference-score ${capability.diff >= 0 ? 'positive' : 'negative'}`}>
                    {shouldHideScore ? '🔒 Locked' : ((capability.diff > 0 ? '+' : '') + (capability.diff || 'N/A'))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Comments Section */}
        <div className="comments-section">
          <h3>Comments</h3>
          <div className="comments-grid">
            <div className="comment-card">
              <h4>Self Comments</h4>
              <div className="comment-content">
                {reviewData.selfComments.map((comment, index) => (
                  <div key={index} className="comment-item">
                    <div className="comment-capability">{comment.capability}</div>
                    {comment.question && (
                      <div className="comment-question">{comment.question}</div>
                    )}
                    <div className="comment-text">{comment.comment}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="comment-card">
              <h4>Manager Comments</h4>
              <div className="comment-content">
                {reviewData.managerComments.map((comment, index) => (
                  <div key={index} className="comment-item">
                    <div className="comment-capability">{comment.capability}</div>
                    {comment.question && (
                      <div className="comment-question">{comment.question}</div>
                    )}
                    <div className="comment-text">{comment.comment}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Action Items Section */}
        <div className="action-items-section">
          <h3>Action Items</h3>
          <div className="action-items-list">
            {reviewData.actionItems.map((item, index) => (
              <div key={index} className="action-item-card">
                <div className="action-item-capability">{item.capability}</div>
                {item.question && (
                  <div className="action-item-question">{item.question}</div>
                )}
                <div className="action-item-text">{item.actionItem}</div>
              </div>
            ))}
          </div>
        </div>


        {/* Action Buttons */}
        <div className="modal-actions">
          <button className="cancel-btn" onClick={onClose}>Cancel</button>
          <button className="save-btn" onClick={handleSave}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default CalibrationModal;
